/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Optimized cart store with instant updates and image caching
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { getCache, setCache, CACHE_TTLS } from '@/lib/global-cache'

export interface CartItem {
  id: string
  productId: string
  quantity: number
  selectedColor?: string
  skuId?: string
  addedAt: number
  isUpdating?: boolean
  lastUpdated?: number
  previousQuantity?: number
}

export interface CartProduct {
  id: string
  name: string
  slug: string
  product_image: string | null
  price_per_kg: number
  kg_price: boolean
  category: string
  concentration?: string
  options?: any
  price_chart?: any
  moq_grams: number
}

interface CartOperation {
  id: string
  type: 'add' | 'update' | 'remove'
  itemId: string
  previousState: any
  timestamp: number
  status: 'pending' | 'success' | 'failed'
}

interface CartState {
  // Core cart data
  items: CartItem[]
  initialProducts: CartProduct[]

  // UI state
  isSidebarOpen: boolean
  showPhoneDialog: boolean
  isLoading: boolean

  // Error handling
  lastError: string | null
  operationInProgress: boolean

  // Optimistic updates
  pendingOperations: CartOperation[]

  // Sync state
  lastSyncTime: number
  syncVersion: number

  // Actions
  addItem: (productId: string, quantity: number, selectedColor?: string, skuId?: string) => void
  removeItem: (itemId: string) => void
  updateQuantity: (itemId: string, quantity: number) => void
  clearCart: () => void

  // Enhanced operations
  updateItemQuantity: (itemId: string, quantity: number) => void
  removeItemById: (itemId: string) => void

  // Error handling
  setError: (error: string | null) => void
  clearError: () => void
  setOperationInProgress: (inProgress: boolean) => void

  // Product management
  setInitialProducts: (products: CartProduct[]) => void
  addProductToCache: (product: CartProduct) => void

  // UI actions
  openSidebar: () => void
  closeSidebar: () => void
  setShowPhoneDialog: (show: boolean) => void
  setLoading: (loading: boolean) => void

  // Sync actions
  syncCart: () => void
  forceSync: () => void

  // Optimistic updates management
  clearPendingOperations: () => void
}

// Custom storage that handles SSR better
const createOptimizedStorage = () => {
  if (typeof window === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => { },
      removeItem: () => { }
    }
  }

  return {
    getItem: (name: string) => {
      try {
        const item = localStorage.getItem(name)
        return item ? JSON.parse(item) : null
      } catch (error) {
        console.warn('Failed to get cart from localStorage:', error)
        return null
      }
    },
    setItem: (name: string, value: any) => {
      try {
        localStorage.setItem(name, JSON.stringify(value))
        sessionStorage.setItem(`${name}_sync`, Date.now().toString())
      } catch (error) {
        console.warn('Failed to save cart to localStorage:', error)
      }
    },
    removeItem: (name: string) => {
      try {
        localStorage.removeItem(name)
        sessionStorage.removeItem(`${name}_sync`)
      } catch (error) {
        console.warn('Failed to remove cart from localStorage:', error)
      }
    }
  }
}

// Helper function to get cached product image
const getCachedProductImage = (productId: string, selectedColor?: string): string | null => {
  const cacheKey = `product-image-${productId}-${selectedColor || 'default'}`
  return getCache<string>(cacheKey)
}

// Helper function to cache product image
const cacheProductImage = (productId: string, imageUrl: string, selectedColor?: string): void => {
  const cacheKey = `product-image-${productId}-${selectedColor || 'default'}`
  setCache(cacheKey, imageUrl, CACHE_TTLS.IMAGES)
}

export const useOptimizedCartStore = create<CartState>()(
  persist(
    immer((set, get) => ({
    // Initial state
    items: [],
    initialProducts: [],
    isSidebarOpen: false,
    showPhoneDialog: false,
    isLoading: false,
    lastError: null,
    operationInProgress: false,
    pendingOperations: [],
    lastSyncTime: Date.now(),
    syncVersion: 1,

    // Add item with instant response and image caching
    addItem: (productId: string, quantity: number, selectedColor?: string, skuId?: string) => {
      const startTime = performance.now()
      console.log('🛒 Adding item to cart:', { productId, quantity, selectedColor, skuId })
      
      set((state) => {
        try {
          state.lastError = null

          if (!productId || quantity <= 0) {
            state.lastError = 'Invalid product or quantity'
            console.error('❌ Invalid product or quantity:', { productId, quantity })
            return
          }

          const existingItemIndex = state.items.findIndex(
            item => item.productId === productId &&
              item.selectedColor === selectedColor &&
              item.skuId === skuId
          )

          if (existingItemIndex >= 0) {
            // Update existing item instantly
            const existingItem = state.items[existingItemIndex]
            existingItem.quantity += quantity
            existingItem.lastUpdated = Date.now()
            console.log('✅ Updated existing item:', existingItem)
          } else {
            // Add new item instantly with better ID generation
            const timestamp = Date.now()
            const newItem: CartItem = {
              id: `${productId}-${selectedColor || 'default'}-${timestamp}-${Math.random().toString(36).substr(2, 9)}`,
              productId,
              quantity,
              selectedColor,
              skuId,
              addedAt: timestamp,
              lastUpdated: timestamp
            }
            state.items.push(newItem)
            console.log('✅ Added new item:', newItem)
            console.log('🛒 Cart now has', state.items.length, 'items')

            // Cache product image if available
            const product = state.initialProducts.find(p => p.id === productId)
            if (product?.product_image) {
              cacheProductImage(productId, product.product_image, selectedColor)
            }
          }

          state.lastSyncTime = Date.now()
          state.syncVersion += 1

          // Performance monitoring
          const endTime = performance.now()
          const duration = endTime - startTime
          console.log(`⚡ Cart addItem completed in ${duration.toFixed(2)}ms`)
          
          if (duration > 100) {
            console.warn(`⚠️ Cart addItem took ${duration.toFixed(2)}ms (>100ms threshold)`)
          }

        } catch (error) {
          console.error('❌ Error adding item to cart:', error)
          state.lastError = error instanceof Error ? error.message : 'Failed to add item to cart'
          
          // Show user-friendly error message
          if (typeof window !== 'undefined') {
            console.error('Cart Error: Failed to add item. Please try again.')
          }
        }
      })
    },

    // Remove item with instant response
    removeItem: (itemId: string) => {
      set((state) => {
        try {
          state.lastError = null

          if (!itemId) {
            state.lastError = 'Invalid item ID'
            return
          }

          state.items = state.items.filter(item => item.id !== itemId)
          state.lastSyncTime = Date.now()
          state.syncVersion += 1

        } catch (error) {
          console.error('❌ Error removing item from cart:', error)
          state.lastError = error instanceof Error ? error.message : 'Failed to remove item'
          
          // Show user-friendly error message
          if (typeof window !== 'undefined') {
            console.error('Cart Error: Failed to remove item. Please try again.')
          }
        }
      })
    },

    // Update quantity with instant response
    updateQuantity: (itemId: string, quantity: number) => {
      const startTime = performance.now()
      
      set((state) => {
        try {
          state.lastError = null

          if (!itemId || quantity < 0) {
            state.lastError = 'Invalid item ID or quantity'
            return
          }

          const item = state.items.find(item => item.id === itemId)
          if (!item) {
            state.lastError = 'Item not found in cart'
            return
          }

          if (quantity === 0) {
            state.items = state.items.filter(item => item.id !== itemId)
          } else {
            item.quantity = quantity
            item.lastUpdated = Date.now()
          }

          state.lastSyncTime = Date.now()
          state.syncVersion += 1

          // Performance monitoring
          const endTime = performance.now()
          const duration = endTime - startTime
          console.log(`⚡ Cart updateQuantity completed in ${duration.toFixed(2)}ms`)
          
          if (duration > 100) {
            console.warn(`⚠️ Cart updateQuantity took ${duration.toFixed(2)}ms (>100ms threshold)`)
          }

        } catch (error) {
          console.error('❌ Error updating quantity:', error)
          state.lastError = error instanceof Error ? error.message : 'Failed to update quantity'
          
          // Show user-friendly error message
          if (typeof window !== 'undefined') {
            console.error('Cart Error: Failed to update quantity. Please try again.')
          }
        }
      })
    },

    // Clear cart
    clearCart: () => {
      set((state) => {
        state.items = []
        state.lastSyncTime = Date.now()
        state.syncVersion += 1
      })
    },

    // Enhanced operations
    updateItemQuantity: (itemId: string, quantity: number) => {
      get().updateQuantity(itemId, quantity)
    },

    removeItemById: (itemId: string) => {
      get().removeItem(itemId)
    },

    // Error handling methods
    setError: (error: string | null) => {
      set((state) => {
        state.lastError = error
      })
    },

    clearError: () => {
      set((state) => {
        state.lastError = null
      })
    },

    setOperationInProgress: (inProgress: boolean) => {
      set((state) => {
        state.operationInProgress = inProgress
      })
    },

    // Product management with image caching
    setInitialProducts: (products: CartProduct[]) => {
      set((state) => {
        state.initialProducts = products

        // Cache product images
        products.forEach(product => {
          if (product.product_image) {
            cacheProductImage(product.id, product.product_image)
          }
        })
      })
    },

    addProductToCache: (product: CartProduct) => {
      set((state) => {
        const existingIndex = state.initialProducts.findIndex(p => p.id === product.id)
        if (existingIndex >= 0) {
          state.initialProducts[existingIndex] = product
        } else {
          state.initialProducts.push(product)
        }

        // Cache product image
        if (product.product_image) {
          cacheProductImage(product.id, product.product_image)
        }
      })
    },

    // UI actions
    openSidebar: () => {
      set((state) => {
        state.isSidebarOpen = true
      })
    },

    closeSidebar: () => {
      set((state) => {
        state.isSidebarOpen = false
      })
    },

    setShowPhoneDialog: (show: boolean) => {
      set((state) => {
        state.showPhoneDialog = show
      })
    },

    setLoading: (loading: boolean) => {
      set((state) => {
        state.isLoading = loading
      })
    },

    // Sync cart across tabs/windows
    syncCart: () => {
      if (typeof window === 'undefined') return

      try {
        const syncTime = sessionStorage.getItem('cart-store_sync')
        const currentState = get()

        if (syncTime && parseInt(syncTime) > currentState.lastSyncTime) {
          const stored = localStorage.getItem('cart-store')
          if (stored) {
            const parsedState = JSON.parse(stored)
            set((state) => {
              Object.assign(state, parsedState.state)
            })
          }
        }
      } catch (error) {
        console.warn('Failed to sync cart:', error)
      }
    },

    // Force sync
    forceSync: () => {
      set((state) => {
        state.lastSyncTime = Date.now()
        state.syncVersion += 1
      })
    },

    // Clear pending operations
    clearPendingOperations: () => {
      set((state) => {
        state.pendingOperations = []
      })
    }
  })),
  {
    name: 'cart-store',
    storage: createJSONStorage(() => createOptimizedStorage()),
    partialize: (state) => ({
      items: state.items,
      initialProducts: state.initialProducts,
      lastSyncTime: state.lastSyncTime,
      syncVersion: state.syncVersion
    }),
    onRehydrateStorage: () => (state) => {
      if (state) {
        console.log('🔄 Cart store rehydrating with items:', state.items?.length || 0)
        
        // Ensure items have proper IDs and timestamps
        if (state.items) {
          state.items = state.items.map(item => ({
            ...item,
            id: item.id || `${item.productId}-${item.selectedColor || 'default'}-${Date.now()}`,
            addedAt: item.addedAt || Date.now(),
            lastUpdated: item.lastUpdated || Date.now()
          }))
        }
        
        // Sync cart after a short delay to allow for proper hydration
        setTimeout(() => {
          state.syncCart()
          state.forceSync()
        }, 100)
      }
    }
  }
))

// Hook for cross-tab synchronization
export function useCartSync() {
  const syncCart = useOptimizedCartStore(state => state.syncCart)

  if (typeof window !== 'undefined') {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'cart-store' || e.key?.includes('cart-store_sync')) {
        console.log('🔄 Cross-tab cart sync triggered by storage change')
        syncCart()
      }
    }

    const handleFocus = () => {
      console.log('🔄 Cross-tab cart sync triggered by window focus')
      syncCart()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('focus', handleFocus)
    
    // Sync every 30 seconds to catch any missed updates
    const interval = setInterval(() => {
      console.log('🔄 Periodic cart sync')
      syncCart()
    }, 30000)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('focus', handleFocus)
      clearInterval(interval)
    }
  }
}

// Hook for cart error handling
export function useCartError() {
  const lastError = useOptimizedCartStore(state => state.lastError)
  const clearError = useOptimizedCartStore(state => state.clearError)
  const setError = useOptimizedCartStore(state => state.setError)

  return {
    error: lastError,
    clearError,
    setError,
    hasError: !!lastError
  }
}

// Hook for cart loading states
export function useCartLoading() {
  const operationInProgress = useOptimizedCartStore(state => state.operationInProgress)
  const isLoading = useOptimizedCartStore(state => state.isLoading)

  return {
    isOperationInProgress: operationInProgress,
    isLoading,
    isAnyLoading: operationInProgress || isLoading
  }
}

// Hook for enhanced cart operations with error handling
export function useCartOperations() {
  const updateItemQuantity = useOptimizedCartStore(state => state.updateItemQuantity)
  const removeItemById = useOptimizedCartStore(state => state.removeItemById)
  const addItem = useOptimizedCartStore(state => state.addItem)
  const clearCart = useOptimizedCartStore(state => state.clearCart)
  const updateQuantity = useOptimizedCartStore(state => state.updateQuantity)
  const removeItem = useOptimizedCartStore(state => state.removeItem)
  const lastError = useOptimizedCartStore(state => state.lastError)
  const clearError = useOptimizedCartStore(state => state.clearError)
  const operationInProgress = useOptimizedCartStore(state => state.operationInProgress)

  const safeUpdateQuantity = (itemId: string, quantity: number) => {
    try {
      clearError()
      updateItemQuantity(itemId, quantity)
    } catch (error) {
      console.error('Failed to update quantity:', error)
    }
  }

  const safeRemoveItem = (itemId: string) => {
    try {
      clearError()
      removeItemById(itemId)
    } catch (error) {
      console.error('Failed to remove item:', error)
    }
  }

  const safeAddItem = (productId: string, quantity: number, selectedColor?: string, skuId?: string) => {
    try {
      clearError()
      addItem(productId, quantity, selectedColor, skuId)
    } catch (error) {
      console.error('Failed to add item:', error)
    }
  }

  return {
    updateItemQuantity: safeUpdateQuantity,
    removeItemById: safeRemoveItem,
    addItem: safeAddItem,
    clearCart,
    updateQuantity,
    removeItem,
    isOperationInProgress: operationInProgress,
    lastError,
    clearError,
    hasError: !!lastError
  }
}

// Hook for item-specific loading states
export function useCartItemState(itemId: string) {
  const item = useOptimizedCartStore(state =>
    state.items.find(item => item.id === itemId)
  )

  return {
    item,
    isUpdating: item?.isUpdating || false,
    exists: !!item
  }
}

// Hook for optimistic updates state
export function useOptimisticUpdates() {
  const pendingOperations = useOptimizedCartStore(state => state.pendingOperations)

  return {
    pendingOperations,
    hasPendingOperations: pendingOperations.length > 0,
    pendingCount: pendingOperations.length,

    getPendingOperationsForItem: (itemId: string) =>
      pendingOperations.filter(op => op.itemId === itemId),

    hasPendingOperationForItem: (itemId: string) =>
      pendingOperations.some(op => op.itemId === itemId),

    getFailedOperations: () =>
      pendingOperations.filter(op => op.status === 'failed')
  }
}

// Hook for enhanced visual feedback
export function useCartVisualFeedback() {
  const operationInProgress = useOptimizedCartStore(state => state.operationInProgress)
  const pendingOperations = useOptimizedCartStore(state => state.pendingOperations)
  const lastError = useOptimizedCartStore(state => state.lastError)

  return {
    isAnyOperationInProgress: operationInProgress,
    hasPendingOperations: pendingOperations.length > 0,
    hasError: !!lastError,

    getItemFeedbackState: (itemId: string) => {
      const item = useOptimizedCartStore.getState().items.find(item => item.id === itemId)
      const hasPendingOp = pendingOperations.some(op => op.itemId === itemId)

      return {
        isUpdating: item?.isUpdating || false,
        hasPendingOperation: hasPendingOp,
        showLoadingSpinner: item?.isUpdating || hasPendingOp,
        showSuccessIndicator: !item?.isUpdating && !hasPendingOp && item?.lastUpdated &&
          (Date.now() - item.lastUpdated) < 2000,
      }
    },

    lastError,
    hasRecentError: !!lastError && lastError.length > 0
  }
}

// Helper function to get cached product image for cart items
export function getCartItemImage(productId: string, selectedColor?: string, fallbackImage?: string): string | null {
  // First try to get from cache
  const cachedImage = getCachedProductImage(productId, selectedColor)
  if (cachedImage) return cachedImage

  // Then try to get from store
  const product = useOptimizedCartStore.getState().initialProducts.find(p => p.id === productId)
  if (product?.product_image) {
    // Cache it for next time
    cacheProductImage(productId, product.product_image, selectedColor)
    return product.product_image
  }

  return fallbackImage || null
}